import {
    IconAlertTriangle,
    IconRefresh,
    IconTimeline
} from '@tabler/icons-react';

import { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, Box, Button, Stack } from '@mantine/core';

import { FirefinchUserStoryDto } from '../../../../api/models';
import {
    useGetApiNotionPhasePhaseIdUserStories,
    useGetApiNotionProjectProjectIdUserStories
} from '../../../../api/notion/notion';
import type { BlockViewProps } from '../../core/types';
import { useDocumentContext } from '../../directives/contexts/DocumentContext';
import { extractCostEstimateData } from '../../directives/utils/costEstimateUtils';
import { MermaidDiagramBox } from '../../mermaid/MermaidDiagramBox';
import {
    type TimelineConfig,
    type TimelinePhase,
    generateTimelineFromPhases
} from '../../mermaid/timelineUtils';

export interface TimelineData {
    mode: 'auto' | 'manual';
    config: TimelineConfig;
    manualChart?: string;
    autoChart?: string;
}

export function TimelineBlockView({
    data,
    onDataChange,
    onDelete,
    readOnly
}: BlockViewProps<TimelineData>) {
    const { document, getCurrentMarkdown } = useDocumentContext();
    const phaseId = document.notionPhaseId;

    const { data: projectUserStories } =
        useGetApiNotionProjectProjectIdUserStories(
            document.projectId,
            undefined,
            { query: { enabled: !!document.projectId && !phaseId } }
        );

    const { data: phaseUserStories } = useGetApiNotionPhasePhaseIdUserStories(
        phaseId ?? '',
        undefined,
        { query: { enabled: !!phaseId } }
    );

    const userStories = phaseId ? phaseUserStories : projectUserStories;

    const [localData, setLocalData] = useState(data);
    const [currentChart, setCurrentChart] = useState(() => {
        if (data.mode === 'manual' && data.manualChart) return data.manualChart;
        if (data.mode === 'auto' && data.autoChart) return data.autoChart;
        return '';
    });
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const saveTimeoutRef = useRef<NodeJS.Timeout>();

    const getCostEstimateData = useCallback(() => {
        try {
            const currentMarkdown =
                getCurrentMarkdown?.() ?? document.content;
            return extractCostEstimateData(currentMarkdown);
        } catch {
            return null;
        }
    }, [getCurrentMarkdown, document.content]);

    const [costEstimateData, setCostEstimateData] = useState(() =>
        getCostEstimateData()
    );

    const saveData = useCallback(
        (newData: TimelineData) => {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(() => {
                onDataChange(newData);
            }, 500);
        },
        [onDataChange]
    );

    const handleGenerateTimeline = useCallback(() => {
        setIsGenerating(true);
        setError(null);

        try {
            const freshCostEstimateData = getCostEstimateData();
            setCostEstimateData(freshCostEstimateData);

            if (
                !freshCostEstimateData?.phases ||
                !freshCostEstimateData?.phaseDurations
            ) {
                throw new Error('No cost estimate data found');
            }

            const phaseMap = new Map<string, FirefinchUserStoryDto[]>();
            if (userStories) {
                userStories.forEach((story) => {
                    const phaseName = story.phase?.name;
                    if (phaseName) {
                        if (!phaseMap.has(phaseName)) {
                            phaseMap.set(phaseName, []);
                        }
                        phaseMap.get(phaseName)!.push(story);
                    }
                });
            }

            const phases: TimelinePhase[] =
                freshCostEstimateData.phases.map((phaseName) => ({
                    name: phaseName,
                    duration:
                        freshCostEstimateData.phaseDurations?.[phaseName] ??
                        localData.config.defaultPhaseDuration,
                    userStories: phaseMap.get(phaseName) ?? [],
                    description: undefined
                }));

            const generatedChart = generateTimelineFromPhases(
                phases,
                localData.config
            );
            setCurrentChart(generatedChart);

            const newData: TimelineData = {
                ...localData,
                mode: 'auto',
                autoChart: generatedChart,
                manualChart: localData.manualChart
            };
            setLocalData(newData);
            saveData(newData);
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to generate timeline'
            );
        } finally {
            setIsGenerating(false);
        }
    }, [getCostEstimateData, userStories, localData, saveData]);

    const handleChartChange = useCallback(
        (newChart: string) => {
            setCurrentChart(newChart);
            const newData: TimelineData = {
                ...localData,
                mode: 'manual',
                manualChart: newChart,
                autoChart: localData.autoChart
            };
            setLocalData(newData);
            saveData(newData);
        },
        [localData, saveData]
    );

    useEffect(() => {
        const freshCostEstimateData = getCostEstimateData();
        if (
            !currentChart &&
            localData.mode === 'auto' &&
            freshCostEstimateData &&
            !isGenerating
        ) {
            setCostEstimateData(freshCostEstimateData);
            handleGenerateTimeline();
        }
    }, [
        currentChart,
        localData.mode,
        isGenerating,
        getCostEstimateData,
        handleGenerateTimeline
    ]);

    useEffect(() => {
        return () => clearTimeout(saveTimeoutRef.current);
    }, []);

    const handleDelete = useCallback(() => {
        if (!readOnly) onDelete();
    }, [onDelete, readOnly]);

    const handleAiGenerate = useCallback(
        (mermaidCode: string) => {
            setCurrentChart(mermaidCode);
            const newData: TimelineData = {
                ...localData,
                mode: 'manual',
                manualChart: mermaidCode,
                autoChart: localData.autoChart
            };
            setLocalData(newData);
            saveData(newData);
        },
        [localData, saveData]
    );

    const emptyAction =
        costEstimateData || getCostEstimateData() ? (
            <Button
                leftSection={<IconRefresh size={16} />}
                onClick={handleGenerateTimeline}
                variant="filled"
            >
                Generate from Cost Estimate
            </Button>
        ) : null;

    const timelineConsiderations = (
        <Alert
            color="blue"
            icon={<IconAlertTriangle size={16} />}
            title="Timeline Projection Considerations"
            variant="light"
            style={{
                border: '2px solid light-dark(var(--mantine-color-blue-4), var(--mantine-color-blue-6))',
                backgroundColor:
                    'light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-6))'
            }}
        >
            <Stack gap="xs">
                <Box>
                    Finalise the cost estimate first, then use{' '}
                    <strong>Regenerate from Cost Estimate</strong> to generate
                    the timeline. Review and adjust the output as required. The
                    timeline can also be edited directly at any time. Note that
                    the cost estimate changes based on the MVP/Non-MVP/All
                    filter selection, and that status will be carried across when
                    you use the regenerate button. If there are multiple cost
                    estimates on the page, the first one will be used.
                </Box>
                <Box
                    component="ul"
                    style={{ margin: 0, paddingLeft: '1.5rem' }}
                >
                    <li>
                        Alternatively, create a timeline manually or use the{' '}
                        <strong>feather icon</strong> to generate a timeline from
                        scratch using AI based on a written description.
                    </li>
                </Box>
                <Box>
                    <strong>Projection notes:</strong> Effort calculations use
                    2-week phases with a 25% adjustment (1/0.8 = 1.25x) to
                    account for holidays, team leave, and other planned absences.
                    Consider additional time for external dependencies,
                    stakeholder review cycles, and testing.
                </Box>
            </Stack>
        </Alert>
    );

    return (
        <MermaidDiagramBox
            title="Project Timeline"
            icon={<IconTimeline size={24} />}
            chart={currentChart}
            onChange={readOnly ? () => { /* read-only */ } : handleChartChange}
            onDelete={handleDelete}
            onRegenerate={
                !readOnly && (costEstimateData || getCostEstimateData())
                    ? handleGenerateTimeline
                    : undefined
            }
            regenerateLabel="Regenerate from Cost Estimate"
            isGenerating={isGenerating}
            error={error}
            emptyMessage={
                !(costEstimateData || getCostEstimateData())
                    ? 'Add a cost estimate first to generate a timeline.'
                    : 'No timeline has been generated yet.'
            }
            emptyAction={readOnly ? undefined : emptyAction}
            onAiGenerate={readOnly ? undefined : handleAiGenerate}
            considerations={timelineConsiderations}
        />
    );
}
